/**
 * PostHog Analytics Utility for donation-elements
 *
 * This utility provides centralized event tracking for donation form interactions.
 * Works with the checkout tracking (checkoutAnalytics.js) to enable complete funnel analysis.
 *
 * Session Continuity:
 * - Session ID is captured via posthog.get_session_id() in form components
 * - Session ID is passed to checkout via postMessage (posthogSessionId field)
 * - Checkout's DonorInfo.jsx registers it as the `parent_session_id` super property
 * - All checkout events then carry parent_session_id for unified funnel analysis
 * - (Not a ?phSession= URL bootstrap — that param is read only by checkout's sentry.js
 *   for Sentry context, unrelated to PostHog session continuity)
 */

import posthog from "posthog-js";
import { prepareClientAnalyticsProperties } from "@charitystack/shared/analytics";

// ============================================================================
// EVENT CONSTANTS
// ============================================================================

export const FORM_EVENTS = {
  // Core form lifecycle events
  FORM_VIEWED: "form:viewed",
  FORM_SUBMITTED: "form:submitted",
  FORM_ERROR: "form:error",

  // User interaction events
  AMOUNT_SELECTED: "form:amount_selected",
  FREQUENCY_CHANGED: "form:frequency_changed",
  FUND_SELECTED: "form:fund_selected",

  // Cart events (for multi-donation flows)
  CART_ITEM_ADDED: "form:cart_item_added",
  CART_ITEM_ADDED_CONFIRMED: "form:cart_item_added_confirmed",
  CART_VIEWED: "form:cart_viewed",

  // Ticket/Event events
  TICKET_SELECTED: "form:ticket_selected",
  TICKET_QUANTITY_CHANGED: "form:ticket_quantity_changed",

  // Product events
  PRODUCT_SELECTED: "form:product_selected",

  // Overlay events
  OVERLAY_OPENED: "form:overlay_opened",
  OVERLAY_CLOSED: "form:overlay_closed",

  // Funnel steps (use with PostHog funnels / drop-off analysis)
  CUSTOM_INPUTS_VIEWED: "form:custom_inputs_viewed",
  /** User passed validation and left the custom inputs step */
  CUSTOM_INPUTS_COMPLETED: "form:custom_inputs_completed",
  SCHEDULED_DONATIONS_VIEWED: "form:scheduled_donations_viewed",
  MONTHLY_BUMP_VIEWED: "form:monthly_bump_viewed",
  /** User chose monthly after one-time upsell (conversion) */
  MONTHLY_BUMP_CONVERTED_TO_MONTHLY: "form:monthly_bump_converted_to_monthly",
  /** User kept one-time amount on bump screen */
  MONTHLY_BUMP_KEPT_ONE_TIME: "form:monthly_bump_kept_one_time",
  MONTHLY_BUMP_BACK: "form:monthly_bump_back",

  // Display currency (P3, SPEC §2.6; properties are snake_case)
  /** Once per form load for an org with conversion on: { display_currency, detected_from, viewer_country } */
  CURRENCY_SHOWN: "form:currency_shown",
  /** The donor picked another currency: { from, to } */
  CURRENCY_SWITCHED: "form:currency_switched",
};

/** Shared PostHog property: filter funnels with donation_funnel = this value */
export const DONATION_ELEMENTS_FUNNEL = "donation_elements_embed";

// Form types for tracking context
export const FORM_TYPES = {
  STANDARD: "standard",
  CROWDFUNDING: "crowdfunding",
  PRODUCT: "product",
  TICKET: "ticket",
  CART: "cart",
};

// Contact Portal Widget events (USER_LOGIN_ELEMENT_PLAN Phase 2 + 2.5)
export const CONTACT_PORTAL_EVENTS = {
  DRAWER_OPENED: "contact_portal:drawer_opened",
  AUTH_STARTED: "contact_portal:auth_started",
  AUTH_COMPLETED: "contact_portal:auth_completed",
  AUTH_FAILED: "contact_portal:auth_failed",
  TAB_VIEWED: "contact_portal:tab_viewed",
  PROFILE_SAVED: "contact_portal:profile_saved",
  SUBSCRIPTION_CANCELED: "contact_portal:subscription_canceled",
  SUBSCRIPTION_REACTIVATED: "contact_portal:subscription_reactivated",
  TAX_RECEIPT_DOWNLOADED: "contact_portal:tax_receipt_downloaded",
  CUSTOM_INPUTS_SAVED: "contact_portal:custom_inputs_saved",
  LOGOUT: "contact_portal:logout",
  // Featured Causes — donor tapped a merchant-curated form card
  FEATURED_CAUSE_CLICKED: "contact_portal:featured_cause_clicked",
  // Phase 2.5 — Email verification
  VERIFY_EMAIL_STARTED: "contact_portal:verify_email_started",
  VERIFY_EMAIL_COMPLETED: "contact_portal:verify_email_completed",
  VERIFY_EMAIL_FAILED: "contact_portal:verify_email_failed",
  // PE-2028 — donor tapped "continue here" on the embed hand-off panel;
  // `granted` property records the requestStorageAccess() outcome.
  STORAGE_ACCESS_RETRY: "contact_portal:storage_access_retry",
};

/**
 * Tie the current PostHog session to a donor identity. Call after successful
 * Clerk auth so sessions + events in the widget are attributable to a specific
 * donor (clerkUserId as the distinct_id).
 */
export const identifyDonor = (clerkUserId, properties = {}) => {
  try {
    if (!clerkUserId || !posthog?.identify) return;
    posthog.identify(clerkUserId, properties);
  } catch (error) {
    console.error("[elementsAnalytics] Error identifying donor:", error);
  }
};

// ============================================================================
// TRACKING UTILITIES
// ============================================================================

/**
 * Track a form event with PostHog
 * Wrapper that adds safety checks and common properties
 *
 * @param {string} eventName - Event name from FORM_EVENTS
 * @param {Object} properties - Event properties
 */
export const trackForm = (eventName, properties = {}) => {
  try {
    // Validate event name
    if (!eventName || typeof eventName !== 'string' || eventName.trim() === '') {
      console.error("[elementsAnalytics] Invalid event name:", eventName);
      return;
    }

    if (!posthog?.capture) {
      console.warn("[elementsAnalytics] PostHog not available for tracking");
      return;
    }

    // Add timestamp for all events (use client_timestamp to avoid collision with user properties)
    const enrichedProperties = prepareClientAnalyticsProperties({
      donation_funnel: DONATION_ELEMENTS_FUNNEL,
      analytics_source: "donation_elements",
      ...properties,
      tracked_at_ms: Date.now(), // Millisecond precision as fallback
    }, { appName: "donation-elements" });

    // Validate payload size to prevent API rejection
    const MAX_PROPERTY_SIZE = 100000; // 100KB limit
    const serialized = JSON.stringify(enrichedProperties);
    if (serialized.length > MAX_PROPERTY_SIZE) {
      console.warn(
        `[elementsAnalytics] Properties too large (${serialized.length} bytes), truncating to ${MAX_PROPERTY_SIZE} bytes`
      );
      // Keep essential properties and truncate the rest
      const essentialProps = {
        client_timestamp: enrichedProperties.client_timestamp,
        tracked_at_ms: enrichedProperties.tracked_at_ms,
        donation_funnel: enrichedProperties.donation_funnel,
        analytics_source: enrichedProperties.analytics_source,
        form_id: enrichedProperties.form_id,
        form_type: enrichedProperties.form_type,
        _truncated: true,
        _original_size: serialized.length,
      };
      posthog.capture(eventName, essentialProps);
      return;
    }

    // Capture event and handle async promise rejection
    const capturePromise = posthog.capture(eventName, enrichedProperties);
    if (capturePromise?.catch) {
      capturePromise.catch((error) => {
        console.error("[elementsAnalytics] PostHog capture promise rejected:", error);
      });
    }

    // Debug logging in development
    if (
      import.meta.env.DEV ||
      import.meta.env.VITE_APP_ENVIRONMENT === "development"
    ) {
      console.log(`[elementsAnalytics] ${eventName}`, enrichedProperties);
    }
  } catch (error) {
    console.error("[elementsAnalytics] Error tracking event:", error);
  }
};

/**
 * Register form context as PostHog super properties
 * These properties are automatically included with every subsequent event
 *
 * @param {Object} formData - Form context data
 * @param {string} [formData.organizationId] - Canonical V2 organization ID
 * @param {string} formData.formId - Unique form identifier
 * @param {string} formData.formName - Form/fundraiser name
 * @param {string} formData.merchantName - Nonprofit organization name
 * @param {string} formData.formType - Form type from FORM_TYPES
 * @param {string} formData.elementId - Element ID (for embedded forms)
 */
export const registerFormContext = ({
  organizationId,
  formId,
  formName,
  merchantName,
  formType,
  elementId,
}) => {
  try {
    if (!posthog?.register) {
      console.warn(
        "[elementsAnalytics] PostHog not available for registering context"
      );
      return;
    }

    if (organizationId && posthog?.group) {
      posthog.group("organization", String(organizationId));
    } else {
      // The donor app can move between embeds in one browser session. Do not
      // let a previous form's organization attach to an ungrouped form.
      posthog?.resetGroups?.();
    }

    const context = {
      organization_id: organizationId || null,
      form_id: formId || null,
      form_name: formName || null,
      form_merchant_name: merchantName || null,
      form_type: formType || null,
      form_element_id: elementId || null,
    };

    // Filter out null values
    const filteredContext = Object.fromEntries(
      Object.entries(context).filter(([, v]) => v != null)
    );

    posthog.register(filteredContext);

    if (
      import.meta.env.DEV ||
      import.meta.env.VITE_APP_ENVIRONMENT === "development"
    ) {
      console.log("[elementsAnalytics] Registered context:", filteredContext);
    }
  } catch (error) {
    console.error("[elementsAnalytics] Error registering context:", error);
  }
};

/**
 * Clear form context super properties
 * Call when form unmounts or user navigates away
 */
export const clearFormContext = () => {
  try {
    if (!posthog) {
      return;
    }

    posthog.resetGroups?.();

    if (!posthog.unregister) return;

    const propertiesToClear = [
      "form_id",
      "organization_id",
      "form_name",
      "form_merchant_name",
      "form_type",
      "form_element_id",
    ];

    propertiesToClear.forEach((prop) => {
      try {
        posthog.unregister(prop);
      } catch (error) {
        console.error(`[elementsAnalytics] Error unregistering property ${prop}:`, error);
      }
    });
  } catch (error) {
    console.error("[elementsAnalytics] Error clearing context:", error);
  }
};

/**
 * Get the current PostHog session ID
 * Used for cross-app session continuity
 *
 * @returns {string|null} Session ID or null if not available
 */
export const getSessionId = () => {
  try {
    return posthog?.get_session_id?.() || null;
  } catch (error) {
    console.error("[elementsAnalytics] Error getting session ID:", error);
    return null;
  }
};
